import { QueryMeta, useQuery, UseQueryResult } from '@tanstack/react-query'
import { getCombos } from './apis'
import { ComboOption } from './types'

/** 중복 제거 + 정렬 — 같은 코드 집합을 다른 순서·다른 컴포넌트에서 요청해도 queryKey 가 같아져
 * react-query 캐시를 공유한다(화면 진입당 요청이 코드 수·호출 지점 수만큼 늘지 않는다). */
const normalizeComboCodes = (comboCodes: string[]): string[] =>
  Array.from(new Set(comboCodes.map((code) => code.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b))

export const comboQueryKeys = {
  all: ['COMBOS'] as const,
  list: (comboCodes: string[]) => [...comboQueryKeys.all, normalizeComboCodes(comboCodes)] as const,
}

const STALE_TIME = 1000 * 60 * 10 // 10분 — 코드성 데이터(commonCode 준용)

/**
 * 콤보 코드별 옵션 조회. 화면 하나가 여러 코드가 필요하면 한 번에 묶어 이 훅을 한 번만 부른다 —
 * API 가 다건 조회를 지원하는데 코드별로 훅을 따로 부르면 요청이 코드 수만큼 늘어난다.
 *
 * 조회 실패는 전역 `queryCache.onError`(src/utils/queryClient.ts)가 알럿을 띄운다. 화면별 커스텀
 * 알럿을 별도로 붙이지 않는다 — 코드성 목록 하나 실패로 화면마다 다른 안내문을 만들 이유가 없고,
 * 전역 알럿으로도 "지금 조회가 실패했다"는 안내는 충분하다(LOGISPRD-12385 PR 본문).
 */
export const useGetCombos = (
  comboCodes: string[],
  /** 실패 알림 방식 — pbo-core 화면은 `{ errorToast: true }` 로 옛 알럿 대신 새 디자인 알럿(notice.failure)을 고른다(queryClient.ts — 2.0 안내 기준 '실패=알럿', 옵션 이름 errorToast 는 옛 이름 그대로). */
  meta?: QueryMeta,
): UseQueryResult<Record<string, ComboOption[]>, Error> => {
  const codes = normalizeComboCodes(comboCodes)
  return useQuery({
    meta,
    queryKey: comboQueryKeys.list(codes),
    queryFn: () => getCombos(codes),
    select: (res) => Object.fromEntries((res.data?.items ?? []).map((item) => [item.comboCode, item.options])),
    enabled: codes.length > 0,
    staleTime: STALE_TIME,
  })
}

/** ComboOption[] → {value,label} 셀렉트 옵션. name 이 비어 있으면(BE nullable) code 로 대체한다. */
export const toSelectOptions = (options: ComboOption[] | undefined): { value: string; label: string }[] =>
  (options ?? []).map((o) => ({ value: o.code, label: o.name ?? o.code }))

/**
 * WH_NM 콤보의 'ALL'(전체) 행을 뺀다. 1.0 dev 실측 상수(GR01·EGR01·GI24 의 옛 WAREHOUSE_*_OPTIONS)가
 * 이 행을 빼고 있었다 — 저장 가능한 실제 센터가 아니라 센터명 편집 셀에 뜨면 안 되기 때문이다
 * (MST_WAREHOUSE 에 없는 값이라 하류 대상 생성에서 조용히 탈락한다, 리뷰 반영 LOGISPRD-12385).
 * `toSelectOptions` 이전에 적용한다. 대소문자를 가리지 않는다 — DB 콜레이션이 대소문자를 무시한다.
 */
export const excludeAllOption = (options: ComboOption[] | undefined): ComboOption[] =>
  (options ?? []).filter((o) => o.code.toUpperCase() !== 'ALL')
